import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authService } from '@/services/authService';
import { useAuthStore, useIsAuthenticated } from '@/stores/authStore';
import {
  CompleteVerificationData,
  ForgotPasswordData,
  ResetPasswordData,
  UserClientRole,
  UserSignupData,
} from '@/types/user';
import { localStorageUtils } from '@/utils/localStorage';
import { sessionStorageUtils } from '@/utils/sessionStorage';
import { notificationKeys } from './useNotificationQueries';
import { poolKeys } from './usePoolQueries';

// Query keys
export const authKeys = {
  all: ['auth'] as const,
  me: () => [...authKeys.all, 'me'] as const,
};

/**
 * Check authentication status
 * Only runs if the session flag is set or Zustand says we're authenticated.
 * Automatically checks every 5 minutes.
 * Can be force refreshed via refetch().
 * Note: State updates are handled in AuthNavigator component
 */
export function useAuthCheck() {
  const isAuthenticated = useIsAuthenticated();

  return useQuery({
    queryKey: authKeys.me(),
    queryFn: async () => {
      const result = await authService.checkAuth();
      if (result.error) throw new Error(result.error.message);
      return result.data;
    },
    retry: false,
    gcTime: 1 * 60 * 1000, // 1 minute
    refetchInterval: 5 * 60 * 1000, // Automatically refetch every 5 minutes
    refetchIntervalInBackground: false,
    enabled: isAuthenticated || localStorageUtils.isSessionAuthenticated(),
  });
}

/**
 * Login mutation
 */
export function useLogin() {
  const queryClient = useQueryClient();
  const { setAuthState } = useAuthStore();

  return useMutation({
    mutationFn: async ({ email, password, role }: { email: string; password: string; role: UserClientRole }) => {
      const result = await authService.login(email, password, role);
      if (result.error) throw new Error(result.error.message);
      return result.data;
    },
    onSuccess: (data) => {
      localStorageUtils.setSessionAuthenticated();
      if (data?.user) {
        setAuthState(data.user, true);
      }
      queryClient.invalidateQueries({ queryKey: authKeys.me() });
    },
  });
}

/**
 * Logout mutation
 */
export function useLogout() {
  const queryClient = useQueryClient();
  const { clearUser } = useAuthStore();

  return useMutation({
    mutationFn: async () => {
      const result = await authService.logout();
      if (result.error) throw new Error(result.error.message);
      return result.data;
    },
    onSuccess: () => {
      localStorageUtils.clearSessionAuthenticated();
      clearUser();
      // Set flag to indicate user just logged out
      sessionStorageUtils.setJustLoggedOut();
      // Remove all queries to clear cache (not just invalidate)
      queryClient.removeQueries({ queryKey: authKeys.me() });

      queryClient.removeQueries({ queryKey: poolKeys.all });
      queryClient.removeQueries({ queryKey: poolKeys.lists() });
      queryClient.removeQueries({ queryKey: poolKeys.details() });

      queryClient.removeQueries({ queryKey: notificationKeys.all });
    },
  });
}

/**
 * Send verification email mutation
 */
export function useSendVerification() {
  return useMutation({
    mutationFn: async (userData: UserSignupData) => {
      const result = await authService.sendVerification(userData);
      if (result.error) throw new Error(result.error.message);
      return result.data;
    },
  });
}

/**
 * Complete verification mutation
 */
export function useCompleteVerification() {
  const queryClient = useQueryClient();
  const { setAuthState } = useAuthStore();

  return useMutation({
    mutationFn: async (data: CompleteVerificationData) => {
      const result = await authService.completeVerification(data);
      if (result.error) throw new Error(result.error.message);
      return result.data;
    },
    onSuccess: (data) => {
      localStorageUtils.setSessionAuthenticated();
      if (data?.user) {
        setAuthState(data.user, true);
      }
      queryClient.invalidateQueries({ queryKey: authKeys.me() });
    },
  });
}

/**
 * Validate qualification code mutation
 */
export function useValidateInvitationCode() {
  const queryClient = useQueryClient();
  const { setAuthState } = useAuthStore();

  return useMutation({
    mutationFn: async (data: { invitation_code: string }) => {
      const result = await authService.validateInvitationCode(data);
      if (result.error) throw new Error(result.error.message);
      return result.data;
    },
    onSuccess: (data) => {
      // Update auth state
      if (data?.user) {
        setAuthState(data.user, true);
      }
      // Invalidate auth query to refetch user data
      queryClient.invalidateQueries({ queryKey: authKeys.me() });
      queryClient.invalidateQueries({ queryKey: poolKeys.lists() });
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
      queryClient.refetchQueries({ queryKey: authKeys.me() });
      queryClient.refetchQueries({ queryKey: poolKeys.lists() });
    },
  });
}

/**
 * Pass Identification check mutation
 */
export function usePassLivenessCheck() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const result = await authService.passLivenessCheck();
      if (result.error) throw new Error(result.error.message);
      return result.data;
    },
    onSuccess: (data) => {
      // Update auth state
      queryClient.invalidateQueries({ queryKey: authKeys.me() });
      queryClient.invalidateQueries({ queryKey: poolKeys.lists() });
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });

      if (data?.verification_url) {
        window.location.href = data.verification_url;
      }
    },
  });
}

/**
 * Resend 2FA code mutation
 */
export function useResend2FACode() {
  return useMutation({
    mutationFn: async (sessionId: string) => {
      const result = await authService.resend2FACode(sessionId);
      if (result.error) throw new Error(result.error.message);
      return result.data;
    },
  });
}

/**
 * Request password reset mutation
 */
export function useForgotPassword() {
  return useMutation({
    mutationFn: async (data: ForgotPasswordData) => {
      const result = await authService.forgotPassword(data);
      if (result.error) throw new Error(result.error.message);
      return result.data;
    },
  });
}

/**
 * Reset password mutation
 */
export function useResetPassword() {
  return useMutation({
    mutationFn: async (data: ResetPasswordData) => {
      const result = await authService.resetPassword(data);
      if (result.error) throw new Error(result.error.message);
      return result.data;
    },
  });
}
